import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Residential", "Commercial", "Industrial", "Renovation / Extension", "Drafting only"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // server response

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSent(data);
      toast.success("Brief received. We'll reply within one business day.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch {
      toast.error("Could not send — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="bg-bone py-24 md:py-40">
      <div className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5 reveal">
          <p className="label">09 — Get in touch</p>
          <h2 className="display mt-6 text-[12vw] md:text-7xl lg:text-[6rem] leading-[0.92] tracking-tighter">
            Start your <span className="italic font-light text-rust">project.</span>
          </h2>
          <p className="mt-8 text-lg text-muted2 max-w-md">
            Tell us about what you{`'`}re building. We{`'`}ll come back within one business day with a clear plan and an honest estimate. Our AI will also send us a structured brief so we hit the ground running.
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <p className="label mb-1">Call</p>
              <a href="tel:+61435510645" className="text-2xl display hover:text-rust transition-colors">0435 510 645</a>
            </div>
            <div>
              <p className="label mb-1">Email</p>
              <a href="mailto:drafting@virarc.com.au" className="text-2xl display hover:text-rust transition-colors">drafting@virarc.com.au</a>
            </div>
            <div>
              <p className="label mb-1">Studio</p>
              <a href="https://www.google.com/maps/dir/?api=1&destination=2%2F36A+Scott+Street+Pooraka+SA+5095+Australia" target="_blank" rel="noreferrer" className="text-xl display hover:text-rust transition-colors">
                2/36A Scott Street, Pooraka SA 5095
              </a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="col-span-12 md:col-span-7 reveal" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
            <Field label="Name" testid="contact-name" value={form.name} onChange={(v) => set("name", v)} />
            <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <Field label="Phone" testid="contact-phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <div>
              <p className="label mb-2">Project type</p>
              <select
                data-testid="contact-type"
                value={form.project_type}
                onChange={(e) => set("project_type", e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 focus:border-rust outline-none py-3 text-lg font-body"
              >
                <option value="">Select…</option>
                {TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
            <div className="md:col-span-2">
              <p className="label mb-2">Message</p>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={5}
                className="w-full bg-transparent border-b border-ink/30 focus:border-rust outline-none py-3 text-lg font-body resize-none"
                placeholder="describe your project, site, budget hint, timeline…"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="px-8 py-4 bg-ink text-bone text-[12px] uppercase tracking-[0.24em] hover:bg-rust transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : "Start your project →"}
            </button>
            <p className="text-sm text-muted2 max-w-sm">
              By submitting, you agree to be contacted by Virarc about your enquiry.
            </p>
          </div>

          {sent?.ai_summary && (
            <div className="mt-10 border border-ink/15 bg-ink/[0.02] p-6" data-testid="contact-ai-brief">
              <p className="label mb-3 text-rust">AI brief preview — what we received</p>
              <pre className="whitespace-pre-wrap font-mono text-sm text-ink/80 leading-relaxed">{sent.ai_summary}</pre>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <p className="label mb-2">{label}</p>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-ink/30 focus:border-rust outline-none py-3 text-lg font-body"
      />
    </div>
  );
}
